import { slugify } from './utils';
import { filtersToQueryString, searchFiltersSchema } from './searchParams';

/**
 * Map a saved search's stored filters (backend/API shape: snake_case, city
 * NAMES, arrays) back to a /homes URL so "Open search" reapplies it.
 */
/** Parse a saved search's API-shape filters into validated URL-state filters. */
export function savedSearchToFilters(filters: Record<string, unknown>) {
  const num = (v: unknown): number | undefined =>
    typeof v === 'number' && Number.isFinite(v) ? v : undefined;
  const str = (v: unknown): string | undefined =>
    typeof v === 'string' && v.trim() ? v.trim() : undefined;
  const list = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x.trim()) : [];

  const cities = list(filters.cities).map((name) => slugify(name)).filter(Boolean);
  const types = list(filters.property_types);

  const parsed = searchFiltersSchema.parse({
    q: str(filters.query),
    cities: cities.length ? cities.join(',') : undefined,
    priceMin: num(filters.price_min),
    priceMax: num(filters.price_max),
    bedsMin: num(filters.beds_min),
    bathsMin: num(filters.baths_min),
    sqftMin: num(filters.sqft_min),
    sqftMax: num(filters.sqft_max),
    types: types.length ? types.join(',') : undefined,
    yearMin: num(filters.year_built_min),
    oh: filters.open_houses_only === true ? '1' : undefined,
    cut: filters.price_cuts_only === true ? '1' : undefined,
    hoaMax: num(filters.hoa_max),
    domMax: num(filters.dom_max),
    zips: list(filters.zips).length ? list(filters.zips).join(',') : undefined,
    bbox: str(filters.bbox),
    sort: str(filters.sort),
  });

  return parsed;
}

export function savedSearchToUrl(filters: Record<string, unknown>): string {
  return `/homes${filtersToQueryString(savedSearchToFilters(filters))}`;
}

/**
 * How a saved search should announce where it came from.
 *
 * Derived here rather than inline in the settings page because that page needs
 * react-query, two zustand stores and the router to render at all — so a rule
 * left in it is covered by no test. `PropertyFacts` sets the precedent: decide
 * in a testable place, render in the view.
 *
 * **Why any of them are badged.** Migration 021 made derived searches REAL saved
 * searches rather than a hidden profile "so the buyer can see it, edit it, or
 * delete it. That visibility is the difference between a helpful product and a
 * creepy one." A search set up by an operator is the sharper case: the person did
 * not merely fail to notice it, they did not make it — and they are about to be
 * emailed from it.
 *
 * Returns null for a search the buyer saved themselves: badging that would be
 * noise, and it is the only one that needs no explanation.
 */
export function searchProvenance(
  source: 'user' | 'auto' | 'admin' | undefined
): { label: string; title: string } | null {
  switch (source) {
    case 'auto':
      return {
        label: 'Automatic',
        title: 'Created automatically from homes you viewed and searched for',
      };
    case 'admin':
      return {
        label: 'Added for you',
        title: 'Set up for you by someone at GoReal — you can edit or delete it',
      };
    default:
      // 'user', and also undefined: an older payload with no source is a search
      // the buyer saved, which is what the column defaults to.
      return null;
  }
}

/** The verb for when a search came into being, which differs by provenance. */
export function searchCreatedVerb(source: 'user' | 'auto' | 'admin' | undefined): string {
  return source === 'user' || source === undefined ? 'Saved' : 'Created';
}
